"use client"

/**
 * Widget Install Instructions
 *
 * Tabbed instructions for embedding the chat widget on different platforms.
 * Only data-token is required — all settings (color, position, etc.) are loaded
 * from the server config automatically.
 */

import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Copy, Check } from "lucide-react"
import { toast } from "sonner"
import { useStorefrontWidgetStatusQuery } from "@/hooks/queries/shopify-storefront-widget"
import { buildWidgetEmbedSnippet } from "@/lib/agents/messaging/channels/web-widget/embed-snippet"
import { ShopifyStorefrontWidget } from "./shopify-storefront-widget"
import { useTranslations } from "next-intl"


interface WidgetInstallInstructionsProps {
  token: string
  channelId: string
  canManage: boolean
}

type Platform = "html" | "shopify" | "wordpress" | "wix"

function getPLATFORMS(): { id: Platform; label: string }[] {
  return [
  { id: "html", label: "HTML" },
  { id: "shopify", label: "Shopify" },
  { id: "wordpress", label: "WordPress" },
  { id: "wix", label: "Wix" },
]
}

export function WidgetInstallInstructions({ token, channelId, canManage }: WidgetInstallInstructionsProps) {
  const PLATFORMS = getPLATFORMS()

  // Opens on Shopify when a store can take the chat in one click; a tab the user picks wins.
  const shopifyStores = useStorefrontWidgetStatusQuery(channelId)
  const [chosenPlatform, setPlatform] = useState<Platform | null>(null)
  const platform = chosenPlatform ?? (shopifyStores.data?.length ? "shopify" : "html")
  const manualInstall = <ManualInstall token={token} platform={platform} />

  return (
    <div className="space-y-2">
      <div className="overflow-x-auto pb-1 [scrollbar-width:thin]">
        <div className="flex w-max gap-0.5 rounded-md bg-muted p-0.5">
        {PLATFORMS.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => setPlatform(p.id)}
            className={`rounded px-2 py-0.5 text-xs transition-colors ${
              platform === p.id
                ? "bg-background font-medium text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {p.label}
          </button>
        ))}
        </div>
      </div>

      {platform === "shopify" ? (
        <ShopifyStorefrontWidget channelId={channelId} canManage={canManage} fallback={manualInstall} />
      ) : (
        manualInstall
      )}
    </div>
  )
}

function ManualInstall({ token, platform }: { token: string; platform: Platform }) {
  const tUi = useTranslations("Messaging.AgentsMessagingSettingsWidgetInstallInstructions")

  const [copied, setCopied] = useState(false)

  const host = typeof window !== "undefined" ? window.location.origin : ""
  const snippet = buildWidgetEmbedSnippet(host, { token, noMinify: platform === "wordpress" })

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(snippet)
      setCopied(true)
      toast.success(tUi("copiedToClipboard"))
      setTimeout(() => setCopied(false), 2000)
    } catch {
      toast.error(tUi("failedToCopy"))
    }
  }

  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">{getSteps(platform)}</p>
      <div className="flex items-center gap-2 rounded-md border bg-background px-2 py-1.5">
        <code className="flex-1 overflow-x-auto whitespace-pre font-mono text-xs">{snippet}</code>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 shrink-0 px-2"
          onClick={handleCopy}
          aria-label={tUi("copyEmbedCode")}
          title={tUi("copyEmbedCode")}
        >
          {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
        </Button>
      </div>
    </div>
  )
}

/** Steps only — the snippet is rendered separately, next to its copy button. */
function getSteps(platform: Platform): string {
  switch (platform) {
    case "html":
      return "Paste before the closing </body> tag of your website."
    case "shopify":
      return "Online Store → Themes → Edit code → theme.liquid, before the closing </body> tag."
    case "wordpress":
      return 'Appearance → Theme Editor → footer.php (or a plugin like "Insert Headers and Footers"), before the closing </body> tag. Keep data-no-minify="1" in the code: it stops WP Rocket from copying the chat script to your site, where the chat stops connecting. If WP Rocket combines JavaScript files, also add widget-loader.js under File Optimization → Excluded JavaScript Files, then clear the WP Rocket cache.'
    case "wix":
      return 'Settings → Custom Code → Add Custom Code. Placement "Body - end", apply to all pages.'
  }
}
